/**
 * Change Order pricing helpers (NZ GST 15%).
 *
 * Authoritative persisted fields in jphph_change_orders:
 * - client_price — Client Price / Credit (incl. GST), shown to clients
 * - builder_cost — Builder cost / reduction (excl. GST), internal only
 *
 * Sign convention:
 * - Addition: both values are >= 0
 * - Credit / Omission: both values are <= 0 (absolute amounts entered positive in the form)
 *
 * There are no separate gst_amount / client_price_excl columns in the
 * production schema; excl/GST breakdowns are derived for UI only.
 */

export const NZ_GST_RATE = 0.15;

export type ChangeOrderPricingMode = 'auto' | 'manual';
export type ChangeOrderPricingType = 'addition' | 'credit';

export interface ChangeOrderPricingTextHints {
  title?: string | null;
  reason?: string | null;
  type?: string | null;
}

/** Existing credit / omission wording used to infer Credit mode for historical rows. */
export const CREDIT_TEXT_PATTERN =
  /\b(credit|omission|omit|reduction|deduct(?:ion)?|credit\s*note|pc\s*sum\s*credit)\b/i;

export function roundCurrency(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function fromExclGst(exclAmount: number): { excl: number; gst: number; incl: number } {
  const excl = roundCurrency(exclAmount);
  const gst = roundCurrency(excl * NZ_GST_RATE);
  const incl = roundCurrency(excl + gst);
  return { excl, gst, incl };
}

export function fromInclGst(inclAmount: number): { excl: number; gst: number; incl: number } {
  const incl = roundCurrency(inclAmount);
  const excl = roundCurrency(incl / (1 + NZ_GST_RATE));
  const gst = roundCurrency(incl - excl);
  return { excl, gst, incl };
}

export function textImpliesCreditPricing(text?: ChangeOrderPricingTextHints | null): boolean {
  if (!text) return false;
  const blob = [text.type, text.title, text.reason].filter(Boolean).join(' ');
  return blob.length > 0 && CREDIT_TEXT_PATTERN.test(blob);
}

/** Infer Addition vs Credit from persisted signed amounts and optional wording. */
export function inferChangeOrderPricingType(
  builderCost: number | null | undefined,
  clientPrice: number | null | undefined,
  text?: ChangeOrderPricingTextHints | null
): ChangeOrderPricingType {
  if ((builderCost != null && builderCost < 0) || (clientPrice != null && clientPrice < 0)) {
    return 'credit';
  }
  if (textImpliesCreditPricing(text)) return 'credit';
  return 'addition';
}

export function isCreditPricingType(type: ChangeOrderPricingType): boolean {
  return type === 'credit';
}

/** Absolute value for form display (credit rows store negatives). */
export function toAbsoluteMoneyInput(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '';
  return String(Math.abs(value));
}

/**
 * Accept loose currency typing while entering:
 * "", ".", "12", "12.", "12.5", "12.50"
 * Also allow a leading "-" for incomplete typing when negatives are permitted.
 */
export function isMoneyInputPartial(raw: string, options?: { allowNegative?: boolean }): boolean {
  const t = raw.trim();
  if (t === '' || t === '.') return true;
  if (options?.allowNegative && (t === '-' || t === '-.')) return true;
  if (options?.allowNegative) {
    return /^-?\d*\.?\d{0,2}$/.test(t);
  }
  return /^\d*\.?\d{0,2}$/.test(t);
}

export function parseMoneyInput(raw: string): number | null {
  const t = raw.trim();
  if (!t || t === '-' || t === '.' || t === '-.') return null;
  const n = Number(t);
  if (!Number.isFinite(n)) return null;
  return n;
}

export function formatMoneyInput(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '';
  return String(value);
}

export function formatCurrencyAbs(
  value: number | null | undefined,
  options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const digits = options?.minimumFractionDigits ?? 2;
  return Math.abs(value).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: options?.maximumFractionDigits ?? digits,
  });
}

export function formatSignedCurrency(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const abs = Math.abs(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (value < 0) return `-$${abs}`;
  return `$${abs}`;
}

/**
 * Auto client price (incl. GST) from builder cost (excl. GST) with zero margin.
 * Operates on absolute amounts; caller applies Credit sign on persist.
 */
export function autoClientPriceInclFromBuilderCost(builderCostExclAbs: number): number {
  return fromExclGst(Math.abs(builderCostExclAbs)).incl;
}

/**
 * GST-normalized margin on an excl. GST basis:
 * client_price_excl = client_price_incl / 1.15
 * margin_excl = client_price_excl - builder_cost_excl
 *
 * Credit example: (-6900 / 1.15) - (-8050) = 2050 retained.
 */
export function computeMarginExcl(
  builderCostExcl: number | null | undefined,
  clientPriceIncl: number | null | undefined
): number | null {
  if (builderCostExcl == null || clientPriceIncl == null) return null;
  if (!Number.isFinite(builderCostExcl) || !Number.isFinite(clientPriceIncl)) return null;
  const clientExcl = fromInclGst(clientPriceIncl).excl;
  return roundCurrency(clientExcl - builderCostExcl);
}

/**
 * @deprecated Mixed GST bases. Prefer computeMarginExcl for display.
 * Kept for diagnostics: client_price (incl) - builder_cost (excl).
 */
export function computeSignedMargin(
  builderCost: number | null | undefined,
  clientPrice: number | null | undefined
): number | null {
  if (builderCost == null || clientPrice == null) return null;
  if (!Number.isFinite(builderCost) || !Number.isFinite(clientPrice)) return null;
  return roundCurrency(clientPrice - builderCost);
}

const FORM_ABSOLUTE_NEGATIVE_MESSAGE =
  'Enter a positive amount. Credit / omission sign is applied on save.';

export function validateMoneyAmountInput(
  raw: string,
  options?: {
    required?: boolean;
    /** When false (default), form uses positive absolute entry. */
    allowNegative?: boolean;
    label?: string;
    pricingType?: ChangeOrderPricingType;
  }
): string | null {
  const label = options?.label ?? 'Amount';
  const t = raw.trim();
  if (!t) {
    return options?.required ? `${label} is required` : null;
  }
  if (!isMoneyInputPartial(t, { allowNegative: options?.allowNegative }) || t === '.' || t === '-' || t === '-.') {
    return `Enter a valid ${label.toLowerCase()}`;
  }
  const n = parseMoneyInput(t);
  if (n == null || Number.isNaN(n)) return `Enter a valid ${label.toLowerCase()}`;

  if (options?.allowNegative) {
    if (options.pricingType === 'addition' && n < 0) {
      return `${label} cannot be negative for an Addition.`;
    }
    if (options.pricingType === 'credit' && n > 0) {
      return `${label} must be stored as a negative amount for a Credit / Omission.`;
    }
    return null;
  }

  if (n < 0) {
    return FORM_ABSOLUTE_NEGATIVE_MESSAGE;
  }
  return null;
}

export function validateClientPriceInput(
  raw: string,
  options?: { required?: boolean; allowNegative?: boolean; pricingType?: ChangeOrderPricingType }
): string | null {
  return validateMoneyAmountInput(raw, {
    required: options?.required,
    allowNegative: options?.allowNegative,
    pricingType: options?.pricingType,
    label: options?.pricingType === 'credit' ? 'Client Credit' : 'Client Price',
  });
}

export function validateBuilderCostInput(
  raw: string,
  options?: { required?: boolean; allowNegative?: boolean; pricingType?: ChangeOrderPricingType }
): string | null {
  return validateMoneyAmountInput(raw, {
    required: options?.required,
    allowNegative: options?.allowNegative,
    pricingType: options?.pricingType,
    label: options?.pricingType === 'credit' ? 'Builder Cost Reduction' : 'Builder Cost',
  });
}

/**
 * Persist form absolute amounts with the correct sign for Addition/Credit.
 * Form values are always non-negative absolute amounts.
 */
export function toPersistedSignedAmount(
  raw: string,
  pricingType: ChangeOrderPricingType
): number | null {
  const n = parseMoneyInput(raw);
  if (n == null) return null;
  const abs = roundCurrency(Math.abs(n));
  return pricingType === 'credit' ? -abs : abs;
}

/** Persistable client_price (incl. GST) from form absolute string. */
export function toPersistedClientPrice(
  raw: string,
  pricingType: ChangeOrderPricingType = 'addition'
): number | null {
  return toPersistedSignedAmount(raw, pricingType);
}

export function toPersistedBuilderCost(
  raw: string,
  pricingType: ChangeOrderPricingType = 'addition'
): number | null {
  return toPersistedSignedAmount(raw, pricingType);
}

/** Validate signed values that will be written to the database. */
export function validatePersistedChangeOrderPricing(
  pricingType: ChangeOrderPricingType,
  builderCost: number | null,
  clientPrice: number | null
): string | null {
  if (pricingType === 'addition') {
    if (builderCost != null && builderCost < 0) {
      return 'Builder Cost cannot be negative for an Addition.';
    }
    if (clientPrice != null && clientPrice < 0) {
      return 'Client Price cannot be negative for an Addition.';
    }
    return null;
  }

  if (builderCost != null && builderCost > 0) {
    return 'Builder Cost Reduction must be stored as a negative amount.';
  }
  if (clientPrice != null && clientPrice > 0) {
    return 'Client Credit must be stored as a negative amount.';
  }
  return null;
}

export interface PersistedChangeOrderPricing {
  builder_cost: number | null;
  client_price: number | null;
}

/**
 * Single save-path helper: parse absolute form strings, apply Credit sign,
 * then validate persisted signs. Never rejects a Credit for being negative.
 */
export function persistFormPricing(
  builderRaw: string,
  clientRaw: string,
  pricingType: ChangeOrderPricingType,
  options?: { clientRequired?: boolean }
): { pricing: PersistedChangeOrderPricing; error: string | null } {
  const builderErr = validateBuilderCostInput(builderRaw, {
    allowNegative: false,
    pricingType,
  });
  if (builderErr) return { pricing: { builder_cost: null, client_price: null }, error: builderErr };

  const clientErr = validateClientPriceInput(clientRaw, {
    required: options?.clientRequired,
    allowNegative: false,
    pricingType,
  });
  if (clientErr) return { pricing: { builder_cost: null, client_price: null }, error: clientErr };

  const pricing: PersistedChangeOrderPricing = {
    builder_cost: toPersistedBuilderCost(builderRaw, pricingType),
    client_price: toPersistedClientPrice(clientRaw, pricingType),
  };

  const persistedErr = validatePersistedChangeOrderPricing(
    pricingType,
    pricing.builder_cost,
    pricing.client_price
  );
  if (persistedErr) return { pricing, error: persistedErr };

  return { pricing, error: null };
}

export function clientPriceDisplayLabel(
  clientPrice: number | null | undefined,
  pricingType?: ChangeOrderPricingType
): string {
  const type = pricingType ?? inferChangeOrderPricingType(null, clientPrice);
  return type === 'credit' ? 'Client Credit (incl. GST)' : 'Client Price (incl. GST)';
}

export function builderCostDisplayLabel(
  builderCost: number | null | undefined,
  pricingType?: ChangeOrderPricingType
): string {
  const type = pricingType ?? inferChangeOrderPricingType(builderCost, null);
  return type === 'credit' ? 'Builder Cost Reduction (excl. GST)' : 'Builder Cost (excl. GST)';
}

export const LOCKED_CO_STATUSES = ['approved', 'client_approved', 'closed', 'completed', 'cancelled'] as const;

export function isLockedChangeOrderStatus(status: string): boolean {
  return (LOCKED_CO_STATUSES as readonly string[]).includes(status);
}

/**
 * Fields that remain writable on approved / closed Change Orders.
 * Status and approval history must not be included.
 */
export function buildLockedCommercialUpdate(args: {
  builder_cost: number | null;
  client_price: number | null;
  internal_note: string | null;
  invoice?: Record<string, unknown>;
}): Record<string, unknown> {
  return {
    builder_cost: args.builder_cost,
    client_price: args.client_price,
    internal_note: args.internal_note,
    updated_at: new Date().toISOString(),
    ...(args.invoice ?? {}),
  };
}
